import { ColDef, PostSortRowsParams, RowClickedEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useMemo, useRef } from "react";
import { aggridHeaderClass, SmallTitleSpan } from "src/components/common/common";
import useUiuxStore from "src/store/useUiuxStore";
import { CreateCellClickHandler, CreateCellDoubleClickHandler, CustomCellClickAction, CustomCellDoubleClickAction, postSortHandler, useCellChangeHandler, createCellCssClassRules, GridCellConfig } from "src/utils/grid/GridHandlerContent";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { defaultColDef, getDefaultRowIndexColumnConfig } from "src/utils/grid/gridStyleUtils";
import { useKSK1030_R01Store } from "./Store_KSK1030_R01";

import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { KSK1030_R01DetailDto } from "./Dto_KSK1030_R01";

import Grid_Excel from "src/components/common/gridWrapper/Grid_Excel";
import { useDetailTableHandlers as useCommonDetailTableHandlers } from "src/hooks/useDetailTableHandlers";
import { useDetailTableWithItemSearch } from "src/hooks/detail-table/useDetailTableWithItemSearch";
import { createModuleConfig } from "src/utils/detail-table/detailTableUtils";
import { calculateDetailAmount } from "src/utils/calculation/calculationUtils";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { useCodeSearch } from "src/hooks/useCodeSearch";
import { CommonCodeColumn } from "src/utils/grid/GridColumnContent";

const COLUMN_STATE_KEY = 'KSK1030_R01-detail-grid-column-state';

interface Props {
	detailGridRef: React.RefObject<AgGridReact<KSK1030_R01DetailDto> | null>;
	defaultKSK1030_R01DetailRow: KSK1030_R01DetailDto;
	handleFetchLotList: (itemCd: string, itemStatus: string) => void;
}

export const DetailTable_KSK1030_R01 = (props: Props) => {
	const { detailGridRef, defaultKSK1030_R01DetailRow, handleFetchLotList } = props;

	const {
		KSK1030_R01DetailList, setKSK1030_R01DetailList,
		updateKSK1030_R01DetailList,
		selectedKSK1030_R01DetailList, setSelectedKSK1030_R01DetailList,
		initSelectedKSK1030_R01DetailList,
		deletedKSK1030_R01DetailList,
		originalKSK1030_R01DetailList,
		detailColumnState, setDetailColumnState,
		setSelectedItemCdForLot,
		setLotScrollTop,
		editKSK1030_R01,
	} = useKSK1030_R01Store();
	const { unitList, itemStatusList } = useCommonCodeStore();
	const { setCodeSearchModalProps } = useUiuxStore();

	const gridWrapperRef = useRef<HTMLDivElement>(null);

	const {
		getRowId,
		createRowClassRules,
		createCellClassRules,
		paddedGridData,
	} = useGridSettingContent({
		gridRef: detailGridRef,
		key: 'valueIndex',
		gridData: KSK1030_R01DetailList,
		defaultGridData: defaultKSK1030_R01DetailRow,
		selectedRow: selectedKSK1030_R01DetailList,
		originalGridData: originalKSK1030_R01DetailList,
		updateRow: (index: number, data: any) => updateKSK1030_R01DetailList(index, data),
	});

	const headerNames: Record<string, string> = {
		rowIndex: '',
		itemCd: '품목코드',
		itemNm: '품명',
		itemSpec: '규격',
		orderUnit: '단위',
		itemStatus: '상태',
		stockQty: '실재고',
		orderQtyView: '주문잔량',
		updateTransportQty: '출고합계',
		remark: '비고',
	};

	const cssCellConfig = useMemo<GridCellConfig>(() => ({
		disabledCells: ['itemCd', 'itemNm', 'itemSpec', 'itemStatus', 'stockQty', 'modelNo', 'orderUnit', 'orderQty', 'orderQtyView', 'transportQty', 'updateTransportQty'],
		insertedDisabledCells: [''],
		commonDisabledCells: [''],
		pointerCells: ['remark'],
		insertedPointerCells: [''],
		commonPointerCells: [''],
		alignLeftCells: [''],
	}), []);

	const {
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
	} = useMemo(() => createCellCssClassRules(cssCellConfig), [cssCellConfig]);

	const cellCssDefaultColDef = useMemo(() => {
		const baseRules = createCellClassRules<KSK1030_R01DetailDto>({
			idKey: 'valueIndex',
			selectedList: selectedKSK1030_R01DetailList,
			deletedList: deletedKSK1030_R01DetailList,
			disabledInsertedColumns, disabledColumns,
			pointerInsertedColumns, pointerColumns,
			alignLeftColumns,
		});
		return { ...defaultColDef, cellClassRules: { ...baseRules } };
	}, [
		selectedKSK1030_R01DetailList, deletedKSK1030_R01DetailList,
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns,
		alignLeftColumns, createCellClassRules,
	]);

	const defaultEditableColumns = ['remark'];
	const commonInputColumns = ['remark'];

	const baseColumnDefs: ColDef<KSK1030_R01DetailDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{ headerName: '품목코드', colId: 'itemCd', field: 'itemCd', width: 95, editable: false },
		{ headerName: '품명', colId: 'itemNm', field: 'itemNm', width: 150, editable: false },
		{ headerName: '규격', colId: 'itemSpec', field: 'itemSpec', width: 150, editable: false },
		{
			headerName: '단위', colId: 'orderUnit', field: 'orderUnit', width: 50,
			...CommonCodeColumn.options(unitList, false, undefined, undefined),
		},
		{
			headerName: '상태', colId: 'itemStatus', field: 'itemStatus', width: 60,
			...CommonCodeColumn.options(itemStatusList, false, undefined, undefined),
		},
		{
			headerName: '실재고', colId: 'stockQty', field: 'stockQty', width: 90,
			editable: false,
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
			valueFormatter: (params) => params.value?.toLocaleString() ?? '0',
		},
		{
			headerName: '주문잔량', colId: 'orderQtyView', field: 'orderQtyView', width: 95,
			editable: false,
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
			valueFormatter: (params) => params.value?.toLocaleString() ?? '',
		},
		{
			headerName: '출고합계', colId: 'updateTransportQty', field: 'updateTransportQty', width: 95,
			editable: false,
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
			valueFormatter: (params) => params.value?.toLocaleString() ?? '',
		},
		{ headerName: '비고', colId: 'remark', field: 'remark', width: 150, editable: true },
	], [unitList, itemStatusList]);

	const columnDefs: ColDef<KSK1030_R01DetailDto>[] = useMemo(() => {
		if (detailColumnState === null) {
			return baseColumnDefs;
		}
		return GridCustomColumnDefs<KSK1030_R01DetailDto>({
			baseColumnDefs,
			columnState: detailColumnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [baseColumnDefs, detailColumnState, headerNames]);

	const handleCellChange = useCellChangeHandler({
		dataList: KSK1030_R01DetailList,
		originalDataList: originalKSK1030_R01DetailList,
		updateRow: updateKSK1030_R01DetailList,
		customBeforeUpdate: (updatedData) => updatedData,
	});

	const handleRowClick = useCallback((e: RowClickedEvent<KSK1030_R01DetailDto>) => {
		if (e.node.rowPinned === 'bottom') return;
		const rowData = e.data;
		if (!rowData) return;

		const isSelected = selectedKSK1030_R01DetailList.includes(rowData['valueIndex'] as any);

		if (isSelected) {
			setSelectedKSK1030_R01DetailList([]);
			setSelectedItemCdForLot(null);
		} else {
			setSelectedKSK1030_R01DetailList([rowData['valueIndex']] as any);
			const lotKey = `${rowData.itemCd}-${rowData.itemStatus}`;
			setSelectedItemCdForLot(lotKey);
			handleFetchLotList(rowData.itemCd, rowData.itemStatus);
		}
		setLotScrollTop(null);
	}, [selectedKSK1030_R01DetailList, setSelectedKSK1030_R01DetailList, setSelectedItemCdForLot, handleFetchLotList, setLotScrollTop]);

	const cellClickActions: CustomCellClickAction<KSK1030_R01DetailDto>[] = useMemo(() => [
		{
			validate: (field) => commonInputColumns.includes(field),
			action: (e) => { e.api.startEditingCell({ rowIndex: e.rowIndex!, colKey: e.colDef.field! }); },
		},
	], []);
	const handleCellClick = CreateCellClickHandler(KSK1030_R01DetailList, 'valueIndex', cellClickActions);

	const KSK1030Config = createModuleConfig.KSK1030();
	const { searchExactCode } = useCodeSearch({});

	const detailTableHandlers = useCommonDetailTableHandlers({
		detailList: KSK1030_R01DetailList,
		updateDetailList: updateKSK1030_R01DetailList,
		originalDetailList: originalKSK1030_R01DetailList,
		fieldConfig: KSK1030Config.fieldConfig,
		codeSearchConfig: KSK1030Config.codeSearchConfig,
		calculateAmount: (data: any) => calculateDetailAmount(data, {
			qtyField: 'orderQty',
			priceField: 'orderPrice',
			dcField: 'dcAmt',
			amountField: 'orderAmt',
			preventNegative: false,
		}),
		setCodeSearchModalProps: setCodeSearchModalProps,
		gridRef: detailGridRef,
	});

	const cellEventHandlers = useDetailTableWithItemSearch<KSK1030_R01DetailDto>({
		detailList: KSK1030_R01DetailList,
		editableColumns: defaultEditableColumns,
		itemSearchConfig: {
			config: {
				category: '품목',
				responseMapping: {
					itemCd: 'itemCd',
					itemNm: 'itemNm',
					itemSpec: 'itemSpec',
					modelNo: 'modelNo',
					itemUnit: 'orderUnit',
					itemCost: 'orderPrice',
				},
				clearFields: {
					itemCd: "", itemNm: "", itemSpec: "", modelNo: "", orderUnit: "",
					orderPrice: 0, dcRate: 0, dcAmt: 0, orderAmt: 0,
				},
				searchFunction: (code: string, onSuccess?: (item: any) => void) =>
					searchExactCode('item', code, onSuccess),
			},
			settingCodeSearchModalProps: detailTableHandlers.settingCodeSearchModalProps,
		},
		keyDownConfig: {
			qtyField: 'orderQty',
			enableArrowNavigation: true,
		},
	});

	const cellDoubleClickActions: CustomCellDoubleClickAction<KSK1030_R01DetailDto>[] = useMemo(() => [], [editKSK1030_R01?.wmsOrderNo]);
	const handleCellDoubleClick = CreateCellDoubleClickHandler(KSK1030_R01DetailList, 'valueIndex', cellDoubleClickActions);

	const handlePostSort = useCallback((params: PostSortRowsParams<KSK1030_R01DetailDto>) => {
		postSortHandler(params, KSK1030_R01DetailList, originalKSK1030_R01DetailList, setKSK1030_R01DetailList);
	}, [KSK1030_R01DetailList, originalKSK1030_R01DetailList, setKSK1030_R01DetailList]);

	const pinnedBottomRowData = useMemo(() => {
		const totalOrderQtyView = KSK1030_R01DetailList
			.reduce((sum, item) => sum + (deletedKSK1030_R01DetailList.includes(String(item.valueIndex)) ? 0 : Number(item.orderQtyView) || 0), 0);
		const totalUpdateTransportQty = KSK1030_R01DetailList
			.reduce((sum, item) => sum + (deletedKSK1030_R01DetailList.includes(String(item.valueIndex)) ? 0 : Number(item.updateTransportQty) || 0), 0);
		return [{
			itemCd: KSK1030_R01DetailList.length.toLocaleString() + "건",
			orderQtyView: totalOrderQtyView,
			updateTransportQty: totalUpdateTransportQty,
		}];
	}, [KSK1030_R01DetailList, deletedKSK1030_R01DetailList]);

	return (
		<div className="flex flex-1 h-full min-h-full">
			<GridContextWrapper<KSK1030_R01DetailDto>
				gridWrapperRef={gridWrapperRef}
				gridRef={detailGridRef}
				paddedGridData={paddedGridData}
				setPaddedGridData={setKSK1030_R01DetailList}
				initSelected={initSelectedKSK1030_R01DetailList}
				defaultRowData={defaultKSK1030_R01DetailRow}
				baseColumnDefs={columnDefs}
				setColumnState={setDetailColumnState}
				localStorageKey={COLUMN_STATE_KEY}
				menuItems={undefined}
			>
				<div className="w-full h-[32px] shrink-0 [&>span]:!h-[32px] [&>span]:!min-h-[32px] [&>span]:!max-h-[32px] [&>span]:!py-0 [&>span]:box-border">
					<SmallTitleSpan>출고 목록</SmallTitleSpan>
				</div>
				<Grid_Excel<KSK1030_R01DetailDto>
					ref={detailGridRef}
					rowData={paddedGridData} getRowId={getRowId}
					columnDefs={columnDefs}
					defaultColDef={cellCssDefaultColDef}
					rowClassRules={createRowClassRules<KSK1030_R01DetailDto>({
						idKey: 'valueIndex',
						selectedList: selectedKSK1030_R01DetailList,
						deletedList: deletedKSK1030_R01DetailList,
					})}
					onRowClicked={handleRowClick}
					onCellClicked={handleCellClick}
					onCellDoubleClicked={handleCellDoubleClick}
					onCellKeyDown={cellEventHandlers.handleCellKeyDown}
					onCellValueChanged={handleCellChange}
					pinnedBottomRowData={pinnedBottomRowData}
					onPostSortRows={handlePostSort}
					columnStateKey={COLUMN_STATE_KEY}
					setColumnState={setDetailColumnState}
				/>
			</GridContextWrapper>
		</div>
	);
};
